import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Menu, X, Calendar } from 'lucide-react';
import WorkOrderForm from './WorkOrderForm';
import { getRequest } from '../../services/apiService';

// Accept selectedProjectId as a prop
const CalendarScheduler = ({ selectedProjectId }) => {
  const [expandedSections, setExpandedSections] = useState({
    contractors: true,
    employees: true,
    equipment: false
  });

  const [expandedSubSections, setExpandedSubSections] = useState({
    builders: true,
    carpenters: false,
    cement_masons: true,
    drivers: true,
    concrete_mixers: false,
    concrete_pumps: false,
  });

  const [isMobile, setIsMobile] = useState(false);
  const [showResourcePanel, setShowResourcePanel] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showWorkOrderForm, setShowWorkOrderForm] = useState(false);
  const [selectedEventData, setSelectedEventData] = useState(null);
  const [hoverTime, setHoverTime] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [events, setEvents] = useState([]); // Initialize with empty array
  const [myResources, setMyResources] = useState([]); // Initialize with empty array
  const [loading, setLoading] = useState(true); // New loading state
  const [error, setError] = useState(null); // New error state

  // Resizing state variables
  const [resizingEvent, setResizingEvent] = useState(null);
  const [tempEvent, setTempEvent] = useState(null);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [initialMouseX, setInitialMouseX] = useState(0);

  const calendarContentRef = useRef(null);
  const [currentTimeLinePosition, setCurrentTimeLinePosition] = useState(0);

  // Fetch calendar data from API
  useEffect(() => {
    const fetchCalendarData = async () => {
      // Only fetch if a valid project ID is selected (not 'all' or null)
      if (!selectedProjectId || selectedProjectId === 'all') {
        setEvents([]); // Clear events if 'all' projects is selected
        setMyResources([]); // Clear resources
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const startDate = currentWeekDays[0].toISOString().split('T')[0];
        const endDate = currentWeekDays[4].toISOString().split('T')[0]; // Assuming 5 days in week (Mon-Fri)

        // Use selectedProjectId from props
        const endpoint = `/config/work-calendar/?start_date=${startDate}&end_date=${endDate}&project_id=${selectedProjectId}`;
        const data = await getRequest(endpoint);

        setMyResources(data.resources);
        setEvents(data.events.map(event => ({
          ...event,
          start: event.start.includes('T') ? event.start : `${event.start}T00:00:00`, // Ensure valid ISO string
          end: event.end.includes('T') ? event.end : `${event.end}T23:59:59`,     // Ensure valid ISO string
        })));
      } catch (err) {
        console.error("Failed to fetch calendar data:", err);
        setError("Failed to load calendar data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchCalendarData();
  }, [currentDate, selectedProjectId]); // Re-fetch when currentDate or selectedProjectId changes


  // Detect screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Get Monday to Friday of current week
  const currentWeekDays = useMemo(() => {
    const referenceDate = new Date(currentDate);
    const dayOfWeek = referenceDate.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : (1 - dayOfWeek);
    const monday = new Date(referenceDate);
    monday.setDate(referenceDate.getDate() + diffToMonday);
    monday.setHours(0, 0, 0, 0);

    const days = [];
    for (let i = 0; i < 5; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      days.push(date);
    }
    return days;
  }, [currentDate]);

  // Scroll to today's column when clicking "Today" button or on initial load
  useEffect(() => {
    const dayColumnWidth = 600;

    if (!isMobile && calendarContentRef.current && currentWeekDays.length > 0) {
      const today = new Date();
      const todayIndex = currentWeekDays.findIndex(day =>
        day.toDateString() === today.toDateString()
      );

      if (todayIndex !== -1) {
        calendarContentRef.current.scrollLeft = todayIndex * dayColumnWidth;
      }
    }
  }, [currentWeekDays, isMobile, currentDate]);

  // Set selected day to today in mobile view
  useEffect(() => {
    if (isMobile && selectedDay === null && currentWeekDays.length > 0) {
      const today = new Date();
      const todayInWeek = currentWeekDays.find(day =>
        day.toDateString() === today.toDateString()
      );
      setSelectedDay(todayInWeek || currentWeekDays[0]);
    }
  }, [currentWeekDays, isMobile, selectedDay]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const toggleSubSection = (subSectionId) => {
    setExpandedSubSections(prev => ({
      ...prev,
      [subSectionId]: !prev[subSectionId]
    }));
  };

  const visibleResources = useMemo(() => {
    const flatten = (resources, level = 0) => {
      let visible = [];
      resources.forEach(resource => {
        visible.push({ ...resource, level, isGroup: !!resource.children });
        if (resource.children &&
            (level === 0 ? expandedSections[resource.id] : expandedSubSections[resource.id])) {
          visible = visible.concat(flatten(resource.children, level + 1));
        }
      });
      return visible;
    };
    return flatten(myResources);
  }, [myResources, expandedSections, expandedSubSections]);

  const renderResourceList = () => (
    <div className="flex-1 overflow-y-auto">
      {visibleResources.map(resource => (
        <div
          key={resource.id}
          className={`h-10 flex items-center border-b border-gray-100 text-sm ${
            resource.isGroup ? 'font-medium text-gray-800' : 'text-gray-600'
          }`}
          style={{ paddingLeft: `${resource.level * 16 + 8}px` }}
        >
          {resource.children ? (
            <button
              onClick={() => resource.level === 0
                ? toggleSection(resource.id)
                : toggleSubSection(resource.id)}
              className="mr-1 flex items-center"
            >
              {resource.level === 0
                ? (expandedSections[resource.id] ?
                    <ChevronDown className="w-3 h-3" /> :
                    <ChevronRight className="w-3 h-3" />)
                : (expandedSubSections[resource.id] ?
                    <ChevronDown className="w-3 h-3" /> :
                    <ChevronRight className="w-3 h-3" />)
              }
              <span className="ml-1">{resource.name}</span>
            </button>
          ) : (
            <span>{resource.name}</span>
          )}
        </div>
      ))}
    </div>
  );

  const hours = [
    '5 AM', '6 AM', '7 AM', '8 AM', '9 AM', '10 AM', '11 AM', '12 PM',
    '1 PM', '2 PM', '3 PM', '4 PM', '5 PM', '6 PM', '7 PM', '8 PM', '9 PM'
  ];

  const totalCalendarHours = 16;

  const filteredEvents = useMemo(() => {
    const weekStart = currentWeekDays[0].getTime();
    const weekEnd = currentWeekDays[currentWeekDays.length - 1].getTime() + (24 * 60 * 60 * 1000) - 1;

    return events.filter(event => {
      const eventDate = new Date(event.start);
      return eventDate.getTime() >= weekStart && eventDate.getTime() <= weekEnd;
    });
  }, [events, currentWeekDays]);

  // Updated event positioning with multi-day support
  const calculateEventPosition = (startTime, endTime, date) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const currentDayStart = new Date(date);
    currentDayStart.setHours(5, 0, 0, 0);
    const currentDayEnd = new Date(date);
    currentDayEnd.setHours(21, 0, 0, 0);
    
    let left = 0;
    let width = 0;
    
    // Event starts before current day
    if (start < currentDayStart) {
      left = 0;
    } else {
      const startHour = start.getHours() + start.getMinutes() / 60;
      left = ((startHour - 5) / 16) * 100;
    }
    
    // Event ends after current day
    if (end > currentDayEnd) {
      width = 100 - left;
    } else {
      const endHour = end.getHours() + end.getMinutes() / 60;
      width = ((endHour - 5) / 16) * 100 - left;
    }
    
    return { left, width };
  };

  const formatDate = (date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const day = date.getDate();
    const dayName = days[date.getDay()];
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    if (isMobile) {
      return `${day} ${dayName}`;
    }

    return `${day} ${month} ${year} ${dayName}`;
  };

  const formatCurrency = (amount) => {
    if (isMobile) {
      return `$${(amount / 1000).toFixed(0)}k`;
    }
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const calculateDailyRevenue = (date) => {
    const total = filteredEvents
      .filter(event => new Date(event.start).toDateString() === date.toDateString())
      .reduce((sum, event) => sum + event.cost, 0);

    return formatCurrency(total);
  };

  const scrollToToday = () => {
    setCurrentDate(new Date());
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  // Update timeline position every minute
  useEffect(() => {
    const updateLinePosition = () => {
      const now = new Date();
      const currentHour = now.getHours() + now.getMinutes() / 60;
      const gridStart = 5;
      const currentPos = ((currentHour - gridStart) / totalCalendarHours) * 100;
      setCurrentTimeLinePosition(currentPos);
    };

    updateLinePosition();
    const interval = setInterval(updateLinePosition, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleEventClick = (event) => {
    setSelectedEventData(event);
    setShowWorkOrderForm(true);
  };

  const handleCloseWorkOrderForm = () => {
    setShowWorkOrderForm(false);
    setSelectedEventData(null);
  };

  const handleSaveEvent = (updatedEvent) => {
    setEvents(prevEvents =>
      prevEvents.map(event =>
        event.id === updatedEvent.id
          ? updatedEvent
          : event
      )
    );
    handleCloseWorkOrderForm();
  };

  const handleDeleteEvent = (eventIdToDelete) => {
    setEvents(prevEvents => prevEvents.filter(event => event.id !== eventIdToDelete));
    handleCloseWorkOrderForm();
  };

  // Handle mouse movement for time tooltip
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = (x / rect.width) * 100;

    // Calculate time based on position
    const hour = 5 + (percent * 16) / 100;
    const hourInt = Math.floor(hour);
    const minutes = Math.floor((hour - hourInt) * 60);
    const period = hourInt >= 12 ? 'PM' : 'AM';
    const hour12 = hourInt % 12 || 12;
    const timeStr = `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;

    setHoverTime(timeStr);
    setTooltipPosition({ x: e.clientX, y: e.clientY });
  };

  // Resize event functionality
  const handleResizeStart = (e, event, resourceId, direction) => {
    e.stopPropagation();
    setResizingEvent(event);
    setTempEvent({ ...event });
    setResizeDirection(direction);
    setInitialMouseX(e.clientX);
  };

  useEffect(() => {
    if (!resizingEvent) return;

    const handleMouseMove = (e) => {
      if (!resizingEvent || !tempEvent) return;
      
      const dayColumnWidth = 600;
      const pixelsPerHour = dayColumnWidth / totalCalendarHours;
      const deltaX = e.clientX - initialMouseX;
      const deltaHours = deltaX / pixelsPerHour;
      
      const updatedEvent = { ...tempEvent };
      const currentStart = new Date(updatedEvent.start);
      const currentEnd = new Date(updatedEvent.end);
      const currentDay = new Date(currentStart);
      currentDay.setHours(0, 0, 0, 0);
      
      // Calculate new time based on direction
      if (resizeDirection === 'left') {
        const newStart = new Date(currentStart.getTime() + deltaHours * 60 * 60 * 1000);
        updatedEvent.start = newStart.toISOString();
        
        // Handle multi-day left resize
        if (newStart < currentDay) {
          const prevDay = new Date(currentDay);
          prevDay.setDate(currentDay.getDate() - 1);
          updatedEvent.start = new Date(
            prevDay.getFullYear(),
            prevDay.getMonth(),
            prevDay.getDate(),
            17, 0, 0
          ).toISOString();
        }
      } else {
        const newEnd = new Date(currentEnd.getTime() + deltaHours * 60 * 60 * 1000);
        updatedEvent.end = newEnd.toISOString();
        
        // Handle multi-day right resize
        if (newEnd > new Date(currentDay.getTime() + 24 * 60 * 60 * 1000)) {
          const nextDay = new Date(currentDay);
          nextDay.setDate(currentDay.getDate() + 1);
          updatedEvent.end = new Date(
            nextDay.getFullYear(),
            nextDay.getMonth(),
            nextDay.getDate(),
            9, 0, 0
          ).toISOString();
        }
      }
      
      setTempEvent(updatedEvent);
    };

    const handleMouseUp = () => {
      if (tempEvent) {
        // Update events with the new times
        setEvents(prevEvents => 
          prevEvents.map(e => e.id === tempEvent.id ? tempEvent : e)
        );
      }
      setResizingEvent(null);
      setTempEvent(null);
      setResizeDirection(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingEvent, tempEvent, resizeDirection, initialMouseX]);

  // Month picker component
  const MonthPicker = () => {
    const [pickerDate, setPickerDate] = useState(new Date(currentDate));

    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const getDaysInMonth = (date) => {
      return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date) => {
      return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const handleDateSelect = (day) => {
      const newDate = new Date(pickerDate.getFullYear(), pickerDate.getMonth(), day);
      setCurrentDate(newDate);
      setShowMonthPicker(false);
    };

    const navigateMonth = (direction) => {
      const newDate = new Date(pickerDate);
      newDate.setMonth(pickerDate.getMonth() + direction);
      setPickerDate(newDate);
    };

    const daysInMonth = getDaysInMonth(pickerDate);
    const firstDay = getFirstDayOfMonth(pickerDate);
    const today = new Date();

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-4 w-80">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <h3 className="font-semibold text-blue-600">
              {months[pickerDate.getMonth()]} {pickerDate.getFullYear()}
            </h3>
            <button
              onClick={() => navigateMonth(1)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
              <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Empty cells for days before month starts */}
            {Array.from({ length: firstDay }, (_, i) => (
              <div key={`empty-${i}`} className="h-8"></div>
            ))}

            {/* Days of the month */}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const isToday = today.getDate() === day &&
                              today.getMonth() === pickerDate.getMonth() &&
                              today.getFullYear() === pickerDate.getFullYear();
              const isSelected = currentDate.getDate() === day &&
                                 currentDate.getMonth() === pickerDate.getMonth() &&
                                 currentDate.getFullYear() === pickerDate.getFullYear();

              return (
                <button
                  key={day}
                  onClick={() => handleDateSelect(day)}
                  className={`h-8 text-sm rounded hover:bg-blue-100 ${
                    isSelected ? 'bg-blue-500 text-white' :
                    isToday ? 'bg-blue-100 text-blue-600 font-semibold' :
                    'text-gray-700'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Close button */}
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setShowMonthPicker(false)}
              className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Fixed dayColumnWidth to ensure horizontal scroll
  const dayColumnWidth = useMemo(() => {
    return 600;
  }, []);

  if (loading) {
    return <div className="flex justify-center items-center h-screen text-lg">Loading calendar data...</div>;
  }

  if (error) {
    return <div className="flex justify-center items-center h-screen text-lg text-red-600">{error}</div>;
  }

  // Mobile day view
  const renderMobileView = () => {
    const dayToShow = selectedDay || currentWeekDays[0];
    const dayEvents = filteredEvents.filter(event =>
      new Date(event.start).toDateString() === dayToShow.toDateString()
    );

    return (
      <div className="flex flex-col h-full relative">
        {/* Mobile header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowResourcePanel(!showResourcePanel)}
              className="p-2 hover:bg-gray-100 rounded"
            >
              <Menu className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowMonthPicker(true)}
              className="text-lg font-medium text-blue-600 hover:bg-blue-50 px-2 py-1 rounded flex items-center space-x-1"
            >
              <span>{`${currentDate.toLocaleString('default', { month: 'long' })} ${currentDate.getFullYear()}`}</span>
              <Calendar className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateWeek(-1)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <span className="text-blue-600 font-medium text-sm cursor-pointer" onClick={scrollToToday}>Today</span>
            <button
              onClick={() => navigateWeek(1)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Day selector */}
        <div className="flex overflow-x-auto bg-gray-50 border-b">
          {currentWeekDays.map(date => (
            <button
              key={date.toISOString()}
              onClick={() => setSelectedDay(date)}
              className={`flex-shrink-0 px-4 py-3 text-center border-r border-gray-200 ${
                selectedDay?.toDateString() === date.toDateString() ||
                (!selectedDay && date.toDateString() === currentWeekDays[0].toDateString())
                  ? 'bg-blue-100 text-blue-600'
                  : 'hover:bg-gray-100'
              }`}
            >
              <div className="text-sm font-medium">{formatDate(date)}</div>
              <div className="text-xs text-gray-500">{calculateDailyRevenue(date)}</div>
            </button>
          ))}
        </div>

        {/* Events list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {dayEvents.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No events scheduled for this day
            </div>
          ) : (
            dayEvents.map(event => (
              <div
                key={event.id}
                className="p-4 rounded-lg border border-gray-200 bg-white shadow-sm cursor-pointer"
                style={{ borderLeftColor: event.color, borderLeftWidth: '4px' }}
                onClick={() => handleEventClick(event)}
              >
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-medium text-gray-900">{event.title}</h3>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(event.cost)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">{event.location}</p>
                <div className="flex justify-between items-center text-xs text-gray-500">
                  <span>
                    {new Date(event.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} -
                    {new Date(event.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span>{event.resource.length} resources</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Resource panel overlay */}
        {showResourcePanel && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex">
            <div className="bg-white w-80 h-full overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="font-medium">Resources</h2>
                <button onClick={() => setShowResourcePanel(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-2">
                {renderResourceList()}
              </div>
            </div>
            <div className="flex-1" onClick={() => setShowResourcePanel(false)} />
          </div>
        )}

        {/* Month picker */}
        {showMonthPicker && <MonthPicker />}

        {/* Work Order Form Pop-up */}
        {showWorkOrderForm && selectedEventData && (
          <div className="absolute inset-x-0 bottom-0 top-1/2 bg-white z-50 flex flex-col rounded-t-lg shadow-lg overflow-y-auto">
            <div className="p-4 border-b flex justify-end">
                <button onClick={handleCloseWorkOrderForm} className="p-2 rounded-full hover:bg-gray-100">
                    <X className="w-5 h-5 text-gray-600" />
                </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <WorkOrderForm
                eventData={selectedEventData}
                myResources={myResources}
                onClose={handleCloseWorkOrderForm}
                onSave={handleSaveEvent}
                onDelete={handleDeleteEvent}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  // Desktop view
  const renderDesktopView = () => {
    return (
      <div className="w-full h-screen bg-white flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
          <button
            onClick={() => setShowMonthPicker(true)}
            className="text-xl font-medium text-blue-600 hover:bg-blue-50 px-3 py-2 rounded flex items-center space-x-2"
          >
            <span>{`${currentDate.toLocaleString('default', { month: 'long' })} ${currentDate.getFullYear()}`}</span>
            <Calendar className="w-5 h-5" />
          </button>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateWeek(-1)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <span
              className="text-blue-600 font-medium text-sm cursor-pointer"
              onClick={scrollToToday}
            >
              Today
            </span>
            <button
              onClick={() => navigateWeek(1)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Resource Names Column - Fixed Width */}
          <div className="w-48 bg-gray-50 border-r border-gray-200 flex flex-col flex-shrink-0">
            <div className="h-16 border-b border-gray-200 bg-gray-50"></div>
            <div className="h-12 border-b border-gray-200 bg-gray-50"></div>
            {renderResourceList()}
          </div>

          {/* Calendar Grid */}
          <div className="flex-1 flex flex-col bg-white min-w-0">
            <div
              className="flex-1 overflow-x-auto"
              ref={calendarContentRef}
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setHoverTime(null)}
            >
              {/* Time tooltip */}
              {hoverTime && (
                <div
                  className="fixed bg-blue-600 text-white text-xs py-1 px-2 rounded shadow-lg z-20"
                  style={{
                    left: tooltipPosition.x,
                    top: tooltipPosition.y + 10
                  }}
                >
                  {hoverTime}
                </div>
              )}

              <div className="flex flex-col" style={{ width: `${currentWeekDays.length * dayColumnWidth}px`, minWidth: '100%' }}>
                {/* Day Headers */}
                <div className="h-16 flex border-b border-gray-200 bg-gray-50 flex-shrink-0">
                  {currentWeekDays.map(date => (
                    <div key={date.toISOString()} className="p-3 border-r border-gray-200 flex-shrink-0" style={{ width: `${dayColumnWidth}px` }}>
                      <div className="text-sm font-medium text-gray-800">
                        {formatDate(date)}
                      </div>
                      {/* <div className="text-xs text-gray-500 mt-1">
                        Total: {calculateDailyRevenue(date)}
                      </div> */}
                    </div>
                  ))}
                </div>

                {/* Time Headers */}
                <div className="h-12 flex border-b border-gray-200 bg-gray-50 text-xs text-gray-600 flex-shrink-0">
                  {currentWeekDays.map(date => (
                    <div key={`time-header-${date.toISOString()}`} className="flex h-full border-r border-gray-200 flex-shrink-0" style={{ width: `${dayColumnWidth}px` }}>
                      {hours.map((hour, index) => (
                        <div key={index} className="text-center flex items-center justify-center border-r border-gray-100 text-xs px-1" style={{ width: `${dayColumnWidth / hours.length}px` }}>
                          {hour}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* Calendar Content */}
                <div className="flex-1 overflow-y-auto">
                  <div className="flex">
                    {currentWeekDays.map(date => (
                      <div
                        key={`events-col-${date.toISOString()}`}
                        className="border-r border-gray-200 relative flex-shrink-0 bg-gray-50"
                        style={{ width: `${dayColumnWidth}px` }}
                      >
                        {/* Current time indicator */}
                        {date.toDateString() === new Date().toDateString() && (
                          <div
                            className="absolute top-0 w-0.5 h-full bg-blue-500 z-10"
                            style={{ left: `${currentTimeLinePosition}%` }}
                          >
                            <div className="absolute top-1/2 -translate-y-1/2 left-1 bg-blue-500 text-white text-xs px-1 rounded">
                              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        )}

                        {/* Grid lines for hours */}
                        <div className="absolute inset-0 flex">
                          {Array.from({ length: totalCalendarHours }, (_, i) => (
                            <div key={i} className="border-r border-gray-100" style={{ width: `${dayColumnWidth / totalCalendarHours}px`}}></div>
                          ))}
                        </div>

                        {/* Resource rows and events */}
                        <div className="relative">
                          {visibleResources.map((resource) => (
                            <div
                              key={`${date.toISOString()}-${resource.id}`}
                              className="h-10 border-b border-gray-100 relative"
                            >
                              {!resource.isGroup && (
                                filteredEvents
                                  .filter(event =>
                                    event.resource.includes(resource.id) &&
                                    (new Date(event.start).toDateString() === date.toDateString() ||
                                     new Date(event.end).toDateString() === date.toDateString())
                                  )
                                  .map(event => {
                                    const eventToDisplay = tempEvent && tempEvent.id === event.id ? tempEvent : event;
                                    const { left, width } = calculateEventPosition(
                                      eventToDisplay.start,
                                      eventToDisplay.end,
                                      date
                                    );
                                    
                                    return (
                                      <div
                                        key={`${event.id}-${resource.id}`}
                                        className="absolute h-8 top-1 rounded text-xs flex items-center justify-between px-2 overflow-hidden text-white cursor-pointer"
                                        style={{
                                          left: `${left}%`,
                                          width: `${width}%`,
                                          backgroundColor: eventToDisplay.color,
                                          minWidth: '120px',
                                          zIndex: resizingEvent?.id === event.id ? 30 : 10
                                        }}
                                        onClick={() => handleEventClick(eventToDisplay)}
                                      >
                                        {/* Resize handles */}
                                        <div 
                                          className="absolute left-0 top-0 bottom-0 w-2 cursor-col-resize bg-blue-300 bg-opacity-0 hover:bg-opacity-30 z-20"
                                          onMouseDown={e => handleResizeStart(e, event, resource.id, 'left')}
                                        />
                                        <div 
                                          className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize bg-blue-300 bg-opacity-0 hover:bg-opacity-30 z-20"
                                          onMouseDown={e => handleResizeStart(e, event, resource.id, 'right')}
                                        />
                                        
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium truncate">{eventToDisplay.title}</div>
                                        </div>
                                        <div className="font-medium ml-2 flex-shrink-0">
                                          {formatCurrency(eventToDisplay.cost)}
                                        </div>
                                      </div>
                                    );
                                  })
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Month picker */}
        {showMonthPicker && <MonthPicker />}

        {/* Work Order Form Pop-up */}
        {showWorkOrderForm && selectedEventData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <WorkOrderForm
              eventData={selectedEventData}
              myResources={myResources}
              onClose={handleCloseWorkOrderForm}
              onSave={handleSaveEvent}
              onDelete={handleDeleteEvent}
            />
          </div>
        )}
      </div>
    );
  };

  return isMobile ? renderMobileView() : renderDesktopView();
};

export default CalendarScheduler;